import { useSyncExternalStore } from 'react';

import { PACK_IDS } from '@contagio/engine';
import type { PackId } from '@contagio/engine';

/**
 * Puntos y coleccion de paquetes de quien juega. Vive en el navegador y no en el
 * servidor a proposito: desbloquear un paquete solo cambia la piel de las
 * cartas, nunca las reglas, asi que no justifica una cuenta, ni una contrasena,
 * ni un dato personal guardado en ningun sitio. Quien borre los datos del
 * navegador empieza de cero y quien quiera hacerse trampas a si mismo puede
 * hacerlas; ninguna de las dos cosas estropea la partida de nadie.
 *
 * La coleccion es de quien juega, no de la sala: el anfitrion solo puede elegir
 * los paquetes que tenga, pero el resto de la mesa juega con el que salga,
 * aunque no lo haya desbloqueado. Asi se conoce un paquete antes de pagarlo.
 */

export const PROGRESS_KEY = 'contagio.progress';

/**
 * Lo que da cada cosa. Ganar a los bots paga poco: una mesa de bots se llena
 * sola y se gana en unos minutos, asi que si pagara igual que una partida con
 * gente no habria motivo para buscar a nadie. Perder tambien paga algo, para
 * que una mala racha no deje la coleccion parada.
 */
export const REWARDS = {
  /** Por abrir el juego, una vez al dia. */
  daily: 15,
  /** Partida contra bots solamente. */
  winSolo: 10,
  playSolo: 3,
  /** Partida con al menos otra persona en la mesa. */
  winTable: 40,
  playTable: 10,
} as const;

/**
 * Lo que cuesta cada paquete. El de siempre es gratis: nadie llega a una mesa
 * sin cartas. Los dos de heroes son los mas caros porque son el premio gordo de
 * la coleccion, y van al final aunque en la sala se ofrezcan de los primeros.
 * Al ser un Record de PackId, un paquete nuevo no compila sin su precio.
 */
export const PRICES: Record<PackId, number> = {
  contagio: 0,
  heroes: 500,
  marvel: 500,
  frutas: 60,
  cortafuegos: 90,
  orbita: 120,
  asedio: 150,
  arrecife: 180,
  grimorio: 220,
  jurasico: 260,
  banda: 300,
  piratas: 340,
  dema: 400,
};

export interface Progress {
  /** Puntos sin gastar. */
  points: number;
  unlocked: PackId[];
  /** Dia local (AAAA-MM-DD) en que se cobro el bono de entrar. */
  day: string | null;
  /** Ultima partida contada, "SALA:numero". */
  game: string | null;
}

const EMPTY: Progress = { points: 0, unlocked: [], day: null, game: null };

function count(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
}

function read(): Progress {
  try {
    const raw = localStorage.getItem(PROGRESS_KEY);
    if (!raw) return EMPTY;
    const saved = JSON.parse(raw) as Partial<Progress>;
    const unlocked = saved.unlocked;
    return {
      points: count(saved.points),
      // Lo que se lee es del navegador y puede ser de otra version: un nombre de
      // paquete que ya no existe se descarta en vez de colarse en la interfaz.
      unlocked: Array.isArray(unlocked) ? PACK_IDS.filter((id) => unlocked.includes(id)) : [],
      day: typeof saved.day === 'string' ? saved.day : null,
      game: typeof saved.game === 'string' ? saved.game : null,
    };
  } catch {
    return EMPTY;
  }
}

let progress: Progress = typeof localStorage === 'undefined' ? EMPTY : read();
const listeners = new Set<() => void>();

function save(next: Progress): void {
  progress = next;
  try {
    localStorage.setItem(PROGRESS_KEY, JSON.stringify(next));
  } catch {
    /* modo privado: los puntos duran lo que la pestana */
  }
  for (const fn of listeners) fn();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  // Como el sonido y el idioma, la coleccion es del navegador y no de la
  // pestana: si en otra se desbloquea un paquete, esta adopta lo que decida en
  // vez de escribirle encima con su copia vieja.
  const onStorage = (event: StorageEvent) => {
    if (event.key !== PROGRESS_KEY) return;
    progress = read();
    for (const other of listeners) other();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(fn);
    window.removeEventListener('storage', onStorage);
  };
}

export function getProgress(): Progress {
  return progress;
}

export function useProgress(): Progress {
  return useSyncExternalStore(subscribe, getProgress, () => EMPTY);
}

export function isUnlocked(id: PackId, state: Progress = progress): boolean {
  return PRICES[id] === 0 || state.unlocked.includes(id);
}

/** Cuantos paquetes quedan por desbloquear, para contarlo en el inicio. */
export function lockedCount(state: Progress = progress): number {
  return PACK_IDS.filter((id) => !isUnlocked(id, state)).length;
}

/**
 * El paquete bloqueado mas barato: lo que toca por delante. Null si ya estan
 * todos. En empate manda el orden de PACK_IDS, que es el de la sala.
 */
export function nextPack(state: Progress = progress): { id: PackId; price: number } | null {
  let best: { id: PackId; price: number } | null = null;
  for (const id of PACK_IDS) {
    if (isUnlocked(id, state)) continue;
    if (!best || PRICES[id] < best.price) best = { id, price: PRICES[id] };
  }
  return best;
}

/** Paga el paquete. Devuelve false si no llegan los puntos. */
export function unlock(id: PackId): boolean {
  if (isUnlocked(id)) return true;
  const price = PRICES[id];
  if (progress.points < price) return false;
  save({ ...progress, points: progress.points - price, unlocked: [...progress.unlocked, id] });
  return true;
}

/** El dia de hoy donde se juega: el bono es del dia de quien entra, no del UTC. */
function today(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/**
 * Lo cobrado por entrar en esta visita, para que el inicio pueda anunciarlo. No
 * se guarda: es de la pestana, no del navegador.
 */
let claimed = 0;

/** Bono de entrar, una vez al dia. Devuelve lo que ha sumado, 0 si ya estaba. */
export function claimDaily(): number {
  const day = today();
  if (progress.day === day) return 0;
  claimed = REWARDS.daily;
  save({ ...progress, points: progress.points + REWARDS.daily, day });
  return claimed;
}

export function dailyGain(): number {
  return claimed;
}

export interface GameOutcome {
  won: boolean;
  /** Se acabo el mazo sin ganador: el bote se devuelve entero. */
  tie: boolean;
  /** Personas en la mesa, tu incluido. Los bots no cuentan para el bote. */
  humans: number;
  /** Lo que puso cada persona, 0 si la sala jugaba sin apuestas. */
  bet: number;
}

export interface GameReward {
  /** Lo que da jugar o ganar. */
  points: number;
  /** Lo que se lleva o se deja en el bote, con signo. 0 si no habia bote. */
  bet: number;
  won: boolean;
  /** Lo que queda despues de sumarlo todo, para contarlo en el telon. */
  total: number;
}

/**
 * Cuenta una partida terminada. La llave es la sala y el numero de partida:
 * recargar la pagina con el telon puesto vuelve a pasar por aqui, y sin ella la
 * misma victoria se cobraria tantas veces como se recargue. Devuelve null si ya
 * estaba contada.
 */
export function awardGame(key: string, outcome: GameOutcome): GameReward | null {
  if (progress.game === key) return null;
  const withPeople = outcome.humans > 1;
  const points = outcome.won
    ? withPeople
      ? REWARDS.winTable
      : REWARDS.winSolo
    : withPeople
      ? REWARDS.playTable
      : REWARDS.playSolo;
  // El bote es solo de las personas: los bots no ponen, asi que contra ellos no
  // hay nada que ganar ni que perder por mucha apuesta que traiga la sala.
  const stake = withPeople && !outcome.tie ? outcome.bet : 0;
  const bet = outcome.won ? stake * (outcome.humans - 1) : -stake;
  // Nunca por debajo de cero: el servidor no deja empezar a quien no cubre la
  // apuesta, pero el navegador es el ultimo que cuenta y mas vale que cuadre.
  const total = Math.max(0, progress.points + points + bet);
  save({ ...progress, points: total, game: key });
  return { points, bet, won: outcome.won, total };
}
